import type { CustomerDocumentsRouterProps } from '@pages/customer-documents/CustomerDocumentsRouterPage.types';

import { useAuthenticatedUser } from '@contexts/auth-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { useGetToken } from '@shared/hooks/use-get-token';
import * as React from 'react';

import styles from './InstructCustomerDocumentsRouterPage.module.css';

/**
 * InstructCustomerDocumentsRouterPage - MFE wrapper for the Instruct "Customer training documents" entry
 *
 * This page loads the remote CustomerDocumentsRouter MFE and passes a static
 * title. Unlike Manage, Instruct has no selected organization — access is
 * org-wide (CustomerTrainingDocuments_View_All) — so organizationId is omitted.
 *
 * Guard stack (applied in instruct.routes.tsx):
 * 1. AuthenticatedRoute - User must be logged in
 * 2. FeatureFlagGuard - instructorViewCustomerNonFaaDocuments must be enabled
 * 3. PermissionGuard - CustomerTrainingDocuments_View_All required
 */
const InstructCustomerDocumentsRouterPage = React.memo(
  (): React.ReactElement | null => {
    const authData = useAuthenticatedUser();
    const { permissions } = usePermissions();
    const getToken = useGetToken();

    const [RemoteRouter, setRemoteRouter] =
      React.useState<React.ComponentType<CustomerDocumentsRouterProps> | null>(
        null
      );
    const [loadError, setLoadError] = React.useState<Error | null>(null);

    React.useEffect(() => {
      const loadRouter = async (): Promise<void> => {
        try {
          const { default: Router } =
            await import('document_center_mfe/CustomerDocumentsRouter');
          setRemoteRouter(() => Router);
        } catch (error) {
          console.error('Error loading CustomerDocumentsRouter:', error);
          setLoadError(error as Error);
        }
      };

      loadRouter();
    }, []);

    if (loadError) {
      return <InlineFeedback type="internal-server-error" showGoHome={false} />;
    }

    if (!RemoteRouter) {
      return <ContentLoader />;
    }

    return (
      <div
        data-testid="instruct-customer-documents-router-page"
        className={styles.instructCustomerDocumentsRouterPage}
      >
        <React.Suspense fallback={<ContentLoader />}>
          <RemoteRouter
            getToken={getToken}
            permissions={permissions}
            title="Customer training documents"
            user={authData}
          />
        </React.Suspense>
      </div>
    );
  }
);

InstructCustomerDocumentsRouterPage.displayName =
  'InstructCustomerDocumentsRouterPage';

export { InstructCustomerDocumentsRouterPage };
